'use client'

import React, { useCallback } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useRouter } from 'next/navigation'
import { getLocalLocation } from '@/lib/actions'
import { useAppDispatch, useCart, useCheckout } from '@/lib/hooks'
import { setCustomer } from '@/lib/redux'
import { cn } from '@/lib/utils'

import { CartHeader, CartHeaderActions, Checkout } from '@/components/molecules'

interface CartProps extends React.ComponentPropsWithoutRef<'div'> {
  customer: Customer | null
  cartId?: string
}

export const Cart = ({
  className,
  customer,
  cartId: propCartId,
  ...restProps
}: CartProps) => {
  const dispatch = useAppDispatch()
  const router = useRouter()
  const { getAndUpdateCart, resetCart } = useCart()
  const hasMounted = React.useRef(false)
  const cartRef = React.useRef<HTMLInputElement>(null)
  const [isLoading, setIsLoading] = React.useState(true)
  const {
    queryParams,
    setPaymentMethods,
    getPayments,
    currentStep,
    payment_methods,
    resetCheckout,
  } = useCheckout()
  const { cartId: urlCartId, isCheckout, newSale } = queryParams
  // Use prop cartId if provided, otherwise fall back to URL cartId
  const cartId = propCartId || urlCartId

  const getPaymentMethods = useCallback(async () => {
    if (!payment_methods) {
      await setPaymentMethods()
    }
  }, [setPaymentMethods, payment_methods])

  const { data, isPending: isLocationLoading } = useQuery({
    queryKey: ['isCusRequired'],
    queryFn: () => getLocalLocation(),
    refetchOnMount: true,
    refetchInterval: 60000, // 60 seconds
  })

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  React.useEffect(() => {
    if (cartId && !hasMounted.current) {
      getAndUpdateCart(cartId)

      if (isCheckout) {
        getPaymentMethods()
        getPayments({ cartId })
      }
      setIsLoading(false)
      hasMounted.current = true
    } else {
      setIsLoading(false)
      hasMounted.current = false
    }
  }, [cartId])

  React.useEffect(() => {
    if (customer) {
      dispatch(setCustomer(customer))
    }
  }, [customer, dispatch])

  // Reset checkout and cart when starting a new sale
  React.useEffect(() => {
    if (newSale && cartId) {
      resetCheckout()
      resetCart()
      // Clean up the URL parameter using Next.js router
      const params = new URLSearchParams(window.location.search)
      params.delete('newSale')
      router.replace(`/sale?${params.toString()}`, { scroll: false })
    }
  }, [newSale, cartId, resetCheckout, resetCart, router])

  return (
    <div
      ref={cartRef}
      className={cn(
        'flex min-h-full w-full flex-col gap-3 lg:h-full lg:gap-5',
        className,
      )}
      {...restProps}
    >
      {isLoading && (
        <div className="flex w-full flex-grow items-center justify-center">
          Loading...
        </div>
      )}

      {!isLoading && (
        <>
          {currentStep === 'SELECT_CUSTOMER' && (
            <div className="flex w-full flex-col gap-4 lg:flex-row lg:items-center lg:gap-10">
              <CartHeader />
              <CartHeaderActions />
            </div>
          )}

          <Checkout
            isCusRequired={data?.isCusRequired === 'true'}
            isLoading={isLocationLoading}
          />
        </>
      )}
    </div>
  )
}
